/**
 * WebSocket Context for Podcast Manager
 *
 * Provides a persistent WebSocket connection that:
 * - Establishes connection on app load
 * - Subscribes to all episode updates for the current company
 * - Maintains connection with automatic reconnection
 * - Provides episode update events to all components
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { io, Socket } from 'socket.io-client';
import { getApiBaseUrl } from '../services/api';
// @ts-ignore - Module Federation dynamic import
import { auth } from 'shell/Firebase';
import type { UserPresence, PresenceUpdate } from '@/types/presence.types';

// Derive WebSocket URL from API base URL
const getWebSocketUrl = (): string => {
  const apiUrl = getApiBaseUrl();
  try {
    const url = new URL(apiUrl);
    return `${url.protocol}//${url.host}`;
  } catch {
    return 'http://192.168.1.75:3010';
  }
};

export interface EpisodeUpdate {
  id: string;
  companyId: string;
  status: string;
  title?: string;
  updatedAt: string;
  [key: string]: unknown;
}

export interface ClipUpdate {
  id: string;
  episodeId: string;
  companyId: string;
  status: string;
  position: number;
  articleTitle?: string;
  script?: string | null;
  audioPath?: string | null;
  updatedAt: string;
  [key: string]: unknown;
}

export interface ActivityLogUpdate {
  id: string;
  companyId: string;
  episodeId: string;
  clipId?: string;
  eventType: string;
  source: 'user' | 'system';
  title: string;
  detail: string;
  userId?: string;
  userDisplayName?: string;
  userEmail?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

interface WebSocketContextValue {
  isConnected: boolean;
  isSubscribed: boolean;
  socket: Socket | null;
  connectionError: string | null;
  // Subscribe to updates for a specific episode (for detail views)
  subscribeToEpisode: (episodeId: string) => void;
  unsubscribeFromEpisode: (episodeId: string) => void;
  // Register callbacks for episode updates
  onEpisodeUpdate: (callback: (update: EpisodeUpdate) => void) => () => void;
  // Register callbacks for clip updates
  onClipUpdate: (callback: (update: ClipUpdate) => void) => () => void;
  // Register callbacks for activity log updates
  onActivityLog: (callback: (update: ActivityLogUpdate) => void) => () => void;
  // Presence tracking
  joinPresence: (episodeId: string) => void;
  leavePresence: (episodeId: string) => void;
  onPresenceUpdate: (callback: (update: PresenceUpdate) => void) => () => void;
  getActiveViewers: (episodeId: string) => UserPresence[];
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export const useWebSocket = (): WebSocketContextValue => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const updateCallbacksRef = useRef<Set<(update: EpisodeUpdate) => void>>(new Set());
  const clipUpdateCallbacksRef = useRef<Set<(update: ClipUpdate) => void>>(new Set());
  const activityLogCallbacksRef = useRef<Set<(update: ActivityLogUpdate) => void>>(new Set());
  const presenceCallbacksRef = useRef<Set<(update: PresenceUpdate) => void>>(new Set());
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectingRef = useRef(false);

  // Presence tracking
  const activeViewersRef = useRef<Map<string, UserPresence[]>>(new Map());
  const presenceHeartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const currentPresenceEpisodeRef = useRef<string | null>(null);

  // Initialize socket connection
  const initializeSocket = useCallback(async () => {
    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current || socketRef.current?.connected) {
      return;
    }

    isConnectingRef.current = true;
    const wsUrl = getWebSocketUrl();

    try {
      // Wait for auth to be ready
      const user = auth.currentUser;
      if (!user) {
        // Retry after a delay if no user yet
        reconnectTimeoutRef.current = setTimeout(() => {
          isConnectingRef.current = false;
          initializeSocket();
        }, 2000);
        return;
      }

      const token = await user.getIdToken();

      // Disconnect existing socket if any
      if (socketRef.current) {
        socketRef.current.disconnect();
      }

      // Prefer WebSocket, fall back to polling if needed
      // WebSocket is more efficient and reduces HTTP request overhead
      const socket = io(wsUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],  // Prefer WS, fallback to polling
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        timeout: 20000,
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        isConnectingRef.current = false;

        // Subscribe to all episodes for the company
        socket.emit('subscribe:episodes');

        // Batch all connection state updates into one render
        setConnectionError(null);
        setIsSubscribed(true);
        setIsConnected(true);

        // Start periodic ping for connection health monitoring
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        pingIntervalRef.current = setInterval(() => {
          if (socket.connected) {
            socket.emit('ping');
          }
        }, 30000); // Ping every 30 seconds
      });

      socket.on('disconnect', () => {
        setIsConnected(false);
        setIsSubscribed(false);

        // Clear ping interval on disconnect
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
      });

      socket.on('connect_error', (error) => {
        console.error('[WebSocket Context] Connection error:', error.message);
        setConnectionError(error.message);
        isConnectingRef.current = false;
      });

      socket.on('reconnect', () => {
        // Re-subscribe after reconnection
        socket.emit('subscribe:episodes');

        // Batch all reconnection state updates
        setConnectionError(null);
        setIsSubscribed(true);
        setIsConnected(true);
      });

      socket.on('reconnect_error', (error) => {
        console.error('[WebSocket Context] Reconnection error:', error.message);
      });

      socket.on('reconnect_failed', () => {
        console.error('[WebSocket Context] Reconnection failed after all attempts');
        setConnectionError('Failed to reconnect to server');
      });

      // Listen for episode events
      socket.on('episode:created', (data: EpisodeUpdate) => {
        updateCallbacksRef.current.forEach((callback) => {
          try {
            callback(data);
          } catch (err) {
            console.error('[WebSocket Context] Error in create callback:', err);
          }
        });
      });

      socket.on('episode:updated', (data: EpisodeUpdate) => {
        updateCallbacksRef.current.forEach((callback) => {
          try {
            callback(data);
          } catch (err) {
            console.error('[WebSocket Context] Error in update callback:', err);
          }
        });
      });

      socket.on('episode:deleted', () => {
        // Could notify callbacks here if needed for delete handling
      });

      // Listen for clip events
      socket.on('clip:created', (data: ClipUpdate) => {
        clipUpdateCallbacksRef.current.forEach((callback) => {
          try {
            callback(data);
          } catch (err) {
            console.error('[WebSocket Context] Error in clip create callback:', err);
          }
        });
      });

      socket.on('clip:updated', (data: ClipUpdate) => {
        clipUpdateCallbacksRef.current.forEach((callback) => {
          try {
            callback(data);
          } catch (err) {
            console.error('[WebSocket Context] Error in clip update callback:', err);
          }
        });
      });

      socket.on('clip:deleted', () => {
        // Could notify callbacks here if needed for delete handling
      });

      // Listen for activity log events
      socket.on('activity:created', (data: ActivityLogUpdate) => {
        activityLogCallbacksRef.current.forEach((callback) => {
          try {
            callback(data);
          } catch (err) {
            console.error('[WebSocket Context] Error in activity log callback:', err);
          }
        });
      });

      // Ping/pong for connection health monitoring
      socket.on('pong', () => {
        // Connection healthy
      });

      // Listen for presence updates
      socket.on('presence:update', (data: PresenceUpdate) => {
        // Update the active viewers map
        activeViewersRef.current.set(data.episodeId, data.viewers);

        // Notify all registered presence callbacks
        presenceCallbacksRef.current.forEach((callback) => {
          try {
            callback(data);
          } catch (err) {
            console.error('[WebSocket Context] Error in presence callback:', err);
          }
        });
      });

    } catch (error) {
      console.error('[WebSocket Context] Failed to initialize:', error);
      setConnectionError(error instanceof Error ? error.message : 'Connection failed');
      isConnectingRef.current = false;

      // Retry after delay
      reconnectTimeoutRef.current = setTimeout(() => {
        initializeSocket();
      }, 5000);
    }
  }, []);

  // Subscribe to a specific episode's updates (for detail views)
  const subscribeToEpisode = useCallback((episodeId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('subscribe:episode', episodeId);
    }
  }, []);

  // Unsubscribe from a specific episode's updates
  const unsubscribeFromEpisode = useCallback((episodeId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('unsubscribe:episode', episodeId);
    }
  }, []);

  // Register a callback for episode updates
  const onEpisodeUpdate = useCallback((callback: (update: EpisodeUpdate) => void): (() => void) => {
    updateCallbacksRef.current.add(callback);

    return () => {
      updateCallbacksRef.current.delete(callback);
    };
  }, []);

  // Register a callback for clip updates
  const onClipUpdate = useCallback((callback: (update: ClipUpdate) => void): (() => void) => {
    clipUpdateCallbacksRef.current.add(callback);

    return () => {
      clipUpdateCallbacksRef.current.delete(callback);
    };
  }, []);

  // Register a callback for activity log updates
  const onActivityLog = useCallback((callback: (update: ActivityLogUpdate) => void): (() => void) => {
    activityLogCallbacksRef.current.add(callback);

    return () => {
      activityLogCallbacksRef.current.delete(callback);
    };
  }, []);

  // Internal leave presence function (defined first so joinPresence can depend on it)
  const leavePresenceInternal = useCallback((episodeId: string) => {
    if (!socketRef.current?.connected) return;

    socketRef.current.emit('presence:leave', { episodeId });

    // Clear heartbeat if this is the current episode
    if (currentPresenceEpisodeRef.current === episodeId) {
      if (presenceHeartbeatRef.current) {
        clearInterval(presenceHeartbeatRef.current);
        presenceHeartbeatRef.current = null;
      }
      currentPresenceEpisodeRef.current = null;
    }

    // Clear viewers for this episode
    activeViewersRef.current.delete(episodeId);
  }, []);

  // Join presence for an episode (start announcing that this user is viewing)
  const joinPresence = useCallback((episodeId: string) => {
    if (!socketRef.current?.connected) return;

    const user = auth.currentUser;
    if (!user) return;

    // Leave previous episode presence if any
    if (currentPresenceEpisodeRef.current && currentPresenceEpisodeRef.current !== episodeId) {
      leavePresenceInternal(currentPresenceEpisodeRef.current);
    }

    // Store current presence episode
    currentPresenceEpisodeRef.current = episodeId;

    // Emit join event
    socketRef.current.emit('presence:join', {
      episodeId,
      displayName: user.displayName || 'Anonymous',
      photoURL: user.photoURL,
      email: user.email,
    });

    // Start heartbeat interval (every 30 seconds)
    if (presenceHeartbeatRef.current) {
      clearInterval(presenceHeartbeatRef.current);
    }
    presenceHeartbeatRef.current = setInterval(() => {
      if (socketRef.current?.connected && currentPresenceEpisodeRef.current) {
        socketRef.current.emit('presence:heartbeat', { episodeId: currentPresenceEpisodeRef.current });
      }
    }, 30000);
  }, [leavePresenceInternal]);

  // Leave presence for an episode
  const leavePresence = useCallback((episodeId: string) => {
    leavePresenceInternal(episodeId);
  }, [leavePresenceInternal]);

  // Register a callback for presence updates
  const onPresenceUpdate = useCallback((callback: (update: PresenceUpdate) => void): (() => void) => {
    presenceCallbacksRef.current.add(callback);

    return () => {
      presenceCallbacksRef.current.delete(callback);
    };
  }, []);

  // Get active viewers for an episode
  const getActiveViewers = useCallback((episodeId: string): UserPresence[] => {
    return activeViewersRef.current.get(episodeId) || [];
  }, []);

  // Initialize connection on mount
  useEffect(() => {
    // Listen for auth state changes
    const unsubscribeAuth = auth.onAuthStateChanged((user: any) => {
      if (user) {
        initializeSocket();
      } else {
        // Disconnect if user logs out
        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
        }
        setIsConnected(false);
        setIsSubscribed(false);
      }
    });

    return () => {
      unsubscribeAuth();

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }

      // Clean up presence heartbeat
      if (presenceHeartbeatRef.current) {
        clearInterval(presenceHeartbeatRef.current);
        presenceHeartbeatRef.current = null;
      }

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [initializeSocket]);

  const value = useMemo<WebSocketContextValue>(() => ({
    isConnected,
    isSubscribed,
    socket: socketRef.current,
    connectionError,
    subscribeToEpisode,
    unsubscribeFromEpisode,
    onEpisodeUpdate,
    onClipUpdate,
    onActivityLog,
    joinPresence,
    leavePresence,
    onPresenceUpdate,
    getActiveViewers,
  }), [
    isConnected, isSubscribed, connectionError,
    subscribeToEpisode, unsubscribeFromEpisode,
    onEpisodeUpdate, onClipUpdate, onActivityLog,
    joinPresence, leavePresence, onPresenceUpdate, getActiveViewers,
  ]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
